import { TAB_ID } from './crossTabChannel';

/**
 * "Exactly one tab does this, and not more often than X" — built on two
 * localStorage records:
 *
 *   `{key}:lastRun` — when the work last succeeded anywhere in this browser
 *   `{key}:lease`   — which tab is doing it right now, and until when
 *
 * The lease is a short self-expiring claim rather than a durable leader
 * election, so there is no leader to lose: a tab killed mid-flight simply lets
 * its lease run out, and because it never advanced `lastRun`, the next tab to
 * wake up proceeds normally.
 */

/** A stored stamp further from now than this is treated as corrupt. */
const MAX_PLAUSIBLE_AGE_MS = 24 * 60 * 60 * 1000;

/**
 * Two tabs revealed in the same instant (restoring a window, say) would
 * otherwise read the same empty lease and both write it. The random wait plus
 * the read-back below makes that collision rare, and a lost race costs one
 * redundant GET, never a wrong result.
 */
const CONTENTION_JITTER_MS = 400;

interface LeaseRecord {
  owner: string;
  until: number;
}

export interface CrossTabLease {
  /**
   * `at` stamps a successful run. `failed: true` instead pushes the next
   * attempt out by `retryGapMs` for every tab, so a broken endpoint is not
   * hammered by five tabs at once.
   */
  release(result: { at?: number; failed?: boolean }): void;
}

const readJson = <T>(key: string): T | null => {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
};

const writeJson = (key: string, value: unknown): void => {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage full or blocked. Coordination degrades to per-tab behaviour,
    // which is what this feature did before the lease existed.
  }
};

const removeKey = (key: string): void => {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // nothing we can do, and a stale lease self-expires anyway
  }
};

export const isSharedStorageAvailable = (): boolean => {
  try {
    const probe = '__mwv_lease_probe__';
    window.localStorage.setItem(probe, '1');
    window.localStorage.removeItem(probe);
    return true;
  } catch {
    return false;
  }
};

/** Records that the work just succeeded, without holding a lease. */
export const stampLastRun = (key: string, at: number = Date.now()): void => {
  if (!isSharedStorageAvailable()) return;
  writeJson(`${key}:lastRun`, { at });
};

const isHeldByAnotherTab = (lease: LeaseRecord | null, leaseMs: number): boolean => {
  if (!lease || lease.owner === TAB_ID) return false;
  const until = Number(lease.until);
  if (!Number.isFinite(until)) return false;
  const remaining = until - Date.now();
  // A lease claiming to last longer than the maximum was written by a tab whose
  // clock runs ahead. Ignoring it matters: without this clamp, one skewed tab
  // could block every other tab indefinitely.
  return remaining > 0 && remaining <= leaseMs;
};

const delay = (ms: number) =>
  new Promise<void>((resolve) => {
    window.setTimeout(resolve, ms);
  });

export const tryAcquireLease = async (
  key: string,
  {
    minGapMs,
    leaseMs,
    retryGapMs,
  }: { minGapMs: number; leaseMs: number; retryGapMs: number }
): Promise<CrossTabLease | null> => {
  // Without shared storage there is nothing to coordinate through, so every
  // tab acts alone — the honest degradation, not a silent stall.
  if (!isSharedStorageAvailable()) {
    return { release: () => undefined };
  }

  const lastRunKey = `${key}:lastRun`;
  const leaseKey = `${key}:lease`;

  const lastRun = readJson<{ at: number }>(lastRunKey);
  const at = Number(lastRun?.at);
  if (Number.isFinite(at)) {
    const elapsed = Date.now() - at;
    if (elapsed < 0 || elapsed > MAX_PLAUSIBLE_AGE_MS) {
      // The clock jumped, or another tab stamped a nonsensical value. Drop it
      // and treat the work as never having run rather than trusting arithmetic
      // on a bad number.
      removeKey(lastRunKey);
    } else if (elapsed < minGapMs) {
      // Someone already did this recently; their broadcast is on its way.
      return null;
    }
  }

  if (isHeldByAnotherTab(readJson<LeaseRecord>(leaseKey), leaseMs)) return null;

  await delay(Math.random() * CONTENTION_JITTER_MS);

  if (isHeldByAnotherTab(readJson<LeaseRecord>(leaseKey), leaseMs)) return null;

  writeJson(leaseKey, { owner: TAB_ID, until: Date.now() + leaseMs });

  // Last write wins in localStorage, so the only way to know we hold the lease
  // is to read it back.
  if (readJson<LeaseRecord>(leaseKey)?.owner !== TAB_ID) return null;

  return {
    release: ({ at: succeededAt, failed }) => {
      if (failed) {
        writeJson(lastRunKey, { at: Date.now() - minGapMs + retryGapMs });
      } else {
        writeJson(lastRunKey, { at: succeededAt ?? Date.now() });
      }
      if (readJson<LeaseRecord>(leaseKey)?.owner === TAB_ID) removeKey(leaseKey);
    },
  };
};
